/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ErrorType, getMode, getSearchParams, ResponseData, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import {authorizeBolOAuth} from '../../../util/requestSender';
import ApiKey from '../../../models/apiKeyModel';
import BolProduct from '../../../models/bolProductModel';
import Account from '../../../models/accountModel';
import {APIValidationStatus} from '../../../util/interfaces';
import {onConnectBolOauth} from '../../../util/bolOauthUtil';
import BolOauth from './bolOauth';
import {deleteFromLocal, getFromLocal} from '../../../util/storageUtil';
import {
    CONNECTION_COMPLETE_ROUTE,
    OAUTH_INSIGHTS_ROUTE,
    OAUTH_ROUTE,
    OAUTH_SALES_DASHBOARD_REDIRECT,
    OAUTH_SEARCH_RANKINGS_REDIRECT,
    OAUTH_TYPE_KEY,
    SALES_DASHBOARD_V2_ROUTE,
    SEARCH_RANKINGS_ROUTE,
    SELECTED_ACCOUNT_KEY
} from '../../../util/constants';
import {useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import {OAuthType} from '../../../util/enums';

export default function BolOauthController() {
    const {onChangeContext, accountContext} = useContext(AppContext);
    const {account} = accountContext;
    const [status, setStatus] = useState<APIValidationStatus>(APIValidationStatus.VALIDATING);
    const [error, setError] = useState<ErrorType>(null);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const oauthState = useRef<string | null>(null);

    function onClose() {
        navigate('/facturen');
    }

    function onRetry(evt: React.MouseEvent) {
        onConnectBolOauth(evt.shiftKey);
    }

    function mount() {
        const params = getSearchParams(window.location.search, true);
        oauthState.current = params.get('state');
        if (params.has('error-description')) {
            setError(params.get('error-description'));
            setStatus(APIValidationStatus.ERROR);
        } else if (params.has('code') || getMode() !== 'prod') {
            const code: string = params.get('code') ?? '';
            setStatus(APIValidationStatus.VALIDATING);
            setError(null);
            authorizeCode(code);
        } else {
            setStatus(APIValidationStatus.ERROR);
            setError(t('oauth.codeNotFound', 'We hebben geen autorisatiecode ontvangen.'));
        }
    }

    function authorizeCode(code: string) {
        let accountId = getFromLocal(SELECTED_ACCOUNT_KEY);
        if (!accountId) {
            if (!account) return;
            accountId = account.id;
        }
        let oauthType: OAuthType | null;
        const pathname = window.location.pathname;
        if (pathname === OAUTH_ROUTE) {
            oauthType = OAuthType.INVOICES;
        } else if (pathname === OAUTH_INSIGHTS_ROUTE) {
            oauthType = OAuthType.DATA_INSIGHTS;
        } else {
            oauthType = getFromLocal(OAUTH_TYPE_KEY) as OAuthType | null;
        }
        if (!oauthType) {
            setStatus(APIValidationStatus.ERROR);
            setError(t('oauth.connectionFailed', 'Het koppelen met Bol is niet gelukt.'));
            return;
        }
        // Clear the oauth type to prevent mismatches if a retry is needed
        deleteFromLocal(OAUTH_TYPE_KEY);
        authorizeBolOAuth({
            data: {code, oauth_type: oauthType, account_id: accountId},
            callback: (data: ResponseData) => {
                if (data.success) {
                    const apiKey = new ApiKey(data.api_key);
                    if (account) {
                        const _account = account.clone();
                        _account.replaceApiKey(apiKey);
                        onChangeContext({account: _account});
                    }
                    if (data.is_new) {
                        navigate(CONNECTION_COMPLETE_ROUTE);
                        importProducts(apiKey);
                    } else if (oauthState.current) {
                        switch (oauthState.current) {
                            case OAUTH_SEARCH_RANKINGS_REDIRECT:
                                showSnackbar(t('oauth.bol.statusSuccess', 'Koppeling voltooid'));
                                navigate(SEARCH_RANKINGS_ROUTE);
                                break;
                            case OAUTH_SALES_DASHBOARD_REDIRECT:
                                showSnackbar(t('oauth.bol.statusSuccess', 'Koppeling voltooid'));
                                navigate(SALES_DASHBOARD_V2_ROUTE);
                                break;
                            default:
                                setStatus(APIValidationStatus.VALIDATED);
                                break;
                        }
                    } else setStatus(APIValidationStatus.VALIDATED);
                } else {
                    setStatus(APIValidationStatus.ERROR);
                    setError(data.message ?? t('oauth.connectionFailed', 'Het koppelen met Bol is niet gelukt.'));
                }
            }
        });
    }

    function importProducts(apiKey: ApiKey) {
        BolProduct.importAsync(apiKey.accountId, false, (account: Account | null) => {
            // We don't check success status here, it doesn't matter much if the product import failed here
            setStatus(APIValidationStatus.VALIDATED);
            onChangeContext({account});
        });
    }

    useEffect(() => {
        mount();
    }, []);

    return <BolOauth onClose={onClose} onRetry={onRetry} status={status} error={error} />;
}
